import { Action, action, computed, Computed } from 'easy-peasy';
import type {
  EditorMode,
  NodeKind,
  OpenModal,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowNode,
  WorkflowSnippet,
} from '../types';
import {
  addEdge as addEdgeOp,
  addNode as addNodeOp,
  makeNode,
  removeEdge as removeEdgeOp,
  removeNode as removeNodeOp,
  updateNode as updateNodeOp,
} from '../utils/graph-ops';
import { migrateGraph } from '../utils/migrate-graph';
import {
  expandInatividade,
  expandReenvio,
} from '../utils/snippet-expand';

export interface WorkflowEditorModel {
  graph: WorkflowGraph | null;
  selectedNodeId: string | null;
  selectedSnippetId: string | null;
  mode: EditorMode;
  openModal: OpenModal;
  dirty: boolean;
  /** Stack de estados anteriores pro undo. Limitado a HISTORY_LIMIT. */
  past: WorkflowGraph[];
  /** Stack de estados desfeitos pra redo. Limpa em qualquer nova edição. */
  future: WorkflowGraph[];

  selectedNode: Computed<WorkflowEditorModel, WorkflowNode | null>;
  selectedSnippet: Computed<WorkflowEditorModel, import('../types').WorkflowSnippet | null>;

  // graph lifecycle
  loadGraph: Action<WorkflowEditorModel, WorkflowGraph>;
  setName: Action<WorkflowEditorModel, string>;
  markPristine: Action<WorkflowEditorModel>;
  setOperationType: Action<WorkflowEditorModel, WorkflowGraph['operationType']>;
  setShippingTableIds: Action<WorkflowEditorModel, string[]>;
  setTarget: Action<WorkflowEditorModel, WorkflowGraph['target']>;

  // history
  undo: Action<WorkflowEditorModel>;
  redo: Action<WorkflowEditorModel>;

  // selection / panel
  selectNode: Action<WorkflowEditorModel, string | null>;
  selectSnippet: Action<WorkflowEditorModel, string | null>;
  setOpenModal: Action<WorkflowEditorModel, OpenModal>;

  // node ops
  addNode: Action<
    WorkflowEditorModel,
    { kind: NodeKind; position?: { x: number; y: number } }
  >;
  updateNodePatch: Action<
    WorkflowEditorModel,
    { nodeId: string; patch: Partial<WorkflowNode> }
  >;
  updateNodeConfigPatch: Action<
    WorkflowEditorModel,
    { nodeId: string; configPatch: Record<string, unknown> }
  >;
  removeNode: Action<WorkflowEditorModel, string>;
  removeSnippet: Action<WorkflowEditorModel, string>;
  /** Atualiza só a posição de um nó (drag manual no canvas). Não marca dirty. */
  setNodePosition: Action<WorkflowEditorModel, { nodeId: string; position: { x: number; y: number } }>;
  /** Substitui as posições de todos os nós (usado pelo auto-layout). Não marca dirty. */
  applyAutoLayoutPositions: Action<WorkflowEditorModel, Record<string, { x: number; y: number }>>;

  // edges
  addEdge: Action<
    WorkflowEditorModel,
    {
      fromNodeId: string;
      fromPortId: string;
      toNodeId: string;
      style?: WorkflowEdge['style'];
    }
  >;
  removeEdge: Action<WorkflowEditorModel, string>;

  // connect mode
  startConnect: Action<
    WorkflowEditorModel,
    { sourceNodeId: string; sourcePortId: string; sourceStyle: WorkflowEdge['style'] }
  >;
  cancelConnect: Action<WorkflowEditorModel>;
  completeConnect: Action<WorkflowEditorModel, { toNodeId: string }>;

  // snippets
  addSnippet: Action<
    WorkflowEditorModel,
    {
      kind: WorkflowSnippet['kind'];
      attachToNodeId?: string;
      attachToPortId?: string;
      position?: { x: number; y: number };
    }
  >;
}

const HISTORY_LIMIT = 50;

const setDirty = <T extends { dirty: boolean }>(state: T) => {
  state.dirty = true;
};

/**
 * Snapshot do graph atual no stack de undo. Limpa o stack de redo
 * (qualquer edição depois de um undo descarta a "linha do tempo
 * alternativa"). Chamado no início de toda action que muta o graph.
 */
function pushHistory(state: {
  graph: WorkflowGraph | null;
  past: WorkflowGraph[];
  future: WorkflowGraph[];
}) {
  if (!state.graph) return;
  const next = [...state.past, state.graph];
  if (next.length > HISTORY_LIMIT) next.shift();
  state.past = next;
  state.future = [];
}

const workflowEditorModel: WorkflowEditorModel = {
  graph: null,
  selectedNodeId: null,
  selectedSnippetId: null,
  mode: { kind: 'idle' },
  openModal: null,
  dirty: false,
  past: [],
  future: [],

  selectedNode: computed((state) =>
    state.graph && state.selectedNodeId
      ? state.graph.nodes.find((n) => n.id === state.selectedNodeId) ?? null
      : null,
  ),

  selectedSnippet: computed((state) =>
    state.graph && state.selectedSnippetId
      ? state.graph.snippets.find((s) => s.id === state.selectedSnippetId) ?? null
      : null,
  ),

  loadGraph: action((state, payload) => {
    state.graph = migrateGraph(payload);
    state.selectedNodeId = null;
    state.selectedSnippetId = null;
    state.mode = { kind: 'idle' };
    state.openModal = null;
    state.dirty = false;
    state.past = [];
    state.future = [];
  }),

  undo: action((state) => {
    if (state.past.length === 0 || !state.graph) return;
    const past = [...state.past];
    const previous = past.pop()!;
    state.future = [state.graph, ...state.future].slice(0, HISTORY_LIMIT);
    state.past = past;
    state.graph = previous;
    // Limpa seleção se apontar pra nó que não existe mais
    if (state.selectedNodeId && !previous.nodes.some((n) => n.id === state.selectedNodeId)) {
      state.selectedNodeId = null;
    }
    if (state.selectedSnippetId && !previous.snippets.some((s) => s.id === state.selectedSnippetId)) {
      state.selectedSnippetId = null;
    }
    state.dirty = true;
  }),

  redo: action((state) => {
    if (state.future.length === 0 || !state.graph) return;
    const [next, ...rest] = state.future;
    state.past = [...state.past, state.graph].slice(-HISTORY_LIMIT);
    state.future = rest;
    state.graph = next;
    if (state.selectedNodeId && !next.nodes.some((n) => n.id === state.selectedNodeId)) {
      state.selectedNodeId = null;
    }
    if (state.selectedSnippetId && !next.snippets.some((s) => s.id === state.selectedSnippetId)) {
      state.selectedSnippetId = null;
    }
    state.dirty = true;
  }),

  setName: action((state, name) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph.name = name;
    setDirty(state);
  }),

  markPristine: action((state) => {
    state.dirty = false;
  }),

  setOperationType: action((state, op) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph.operationType = op;
    setDirty(state);
  }),

  setShippingTableIds: action((state, ids) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph.shippingTableIds = ids;
    setDirty(state);
  }),

  setTarget: action((state, target) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph.target = target;
    setDirty(state);
  }),

  selectNode: action((state, id) => {
    state.selectedNodeId = id;
    if (id) state.selectedSnippetId = null;
  }),

  selectSnippet: action((state, id) => {
    state.selectedSnippetId = id;
    if (id) state.selectedNodeId = null;
  }),

  setOpenModal: action((state, m) => {
    state.openModal = m;
  }),

  addNode: action((state, { kind, position }) => {
    if (!state.graph) return;
    pushHistory(state);
    const node = makeNode(kind, position ?? { x: 0, y: 0 });
    state.graph = addNodeOp(state.graph, node);
    state.selectedNodeId = node.id;
    setDirty(state);
  }),

  updateNodePatch: action((state, { nodeId, patch }) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = updateNodeOp(state.graph, nodeId, patch);
    setDirty(state);
  }),

  updateNodeConfigPatch: action((state, { nodeId, configPatch }) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = {
      ...state.graph,
      nodes: state.graph.nodes.map((n) =>
        n.id === nodeId
          ? ({
              ...n,
              config: { ...(n.config as object), ...configPatch } as never,
            } as typeof n)
          : n,
      ),
    };
    setDirty(state);
  }),

  removeNode: action((state, nodeId) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = removeNodeOp(state.graph, nodeId);
    if (state.selectedNodeId === nodeId) state.selectedNodeId = null;
    setDirty(state);
  }),

  setNodePosition: action((state, { nodeId, position }) => {
    if (!state.graph) return;
    // Drop final do drag — esse é um undo step. (Em-vôo não chama essa
    // action, só o mirror local em WorkflowCanvas.)
    pushHistory(state);
    state.graph = {
      ...state.graph,
      nodes: state.graph.nodes.map((n) =>
        n.id === nodeId ? { ...n, position } : n,
      ),
    };
    // posição não conta como mudança "dirty" — UX-only
  }),

  applyAutoLayoutPositions: action((state, positions) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = {
      ...state.graph,
      nodes: state.graph.nodes.map((n) =>
        positions[n.id] ? { ...n, position: positions[n.id] } : n,
      ),
    };
  }),

  removeSnippet: action((state, snippetId) => {
    if (!state.graph) return;
    const snippet = state.graph.snippets.find((s) => s.id === snippetId);
    if (!snippet) return;
    pushHistory(state);
    const nodeIds = new Set(snippet.nodeIds);
    state.graph = {
      ...state.graph,
      nodes: state.graph.nodes.filter((n) => !nodeIds.has(n.id)),
      edges: state.graph.edges.filter(
        (e) => !nodeIds.has(e.fromNodeId) && !nodeIds.has(e.toNodeId),
      ),
      snippets: state.graph.snippets.filter((s) => s.id !== snippetId),
    };
    if (state.selectedSnippetId === snippetId) state.selectedSnippetId = null;
    if (state.selectedNodeId && nodeIds.has(state.selectedNodeId)) {
      state.selectedNodeId = null;
    }
    setDirty(state);
  }),

  addEdge: action((state, { fromNodeId, fromPortId, toNodeId, style }) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = addEdgeOp(state.graph, fromNodeId, fromPortId, toNodeId, style);
    setDirty(state);
  }),

  removeEdge: action((state, edgeId) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = removeEdgeOp(state.graph, edgeId);
    setDirty(state);
  }),

  startConnect: action((state, { sourceNodeId, sourcePortId, sourceStyle }) => {
    state.mode = { kind: 'connecting', sourceNodeId, sourcePortId, sourceStyle };
  }),

  cancelConnect: action((state) => {
    state.mode = { kind: 'idle' };
  }),

  completeConnect: action((state, { toNodeId }) => {
    if (state.mode.kind !== 'connecting' || !state.graph) return;
    const { sourceNodeId, sourcePortId, sourceStyle } = state.mode;
    if (toNodeId === sourceNodeId) {
      state.mode = { kind: 'idle' };
      return;
    }
    pushHistory(state);
    state.graph = addEdgeOp(state.graph, sourceNodeId, sourcePortId, toNodeId, sourceStyle);
    state.mode = { kind: 'idle' };
    setDirty(state);
  }),

  addSnippet: action((state, { kind, attachToNodeId, attachToPortId, position }) => {
    if (!state.graph) return;
    pushHistory(state);
    const origin = position
      ? { x: position.x - 110, y: position.y - 30 }
      : { x: 0, y: 0 };
    const exp = kind === 'reenvio' ? expandReenvio(origin) : expandInatividade(origin);
    let next: WorkflowGraph = {
      ...state.graph,
      nodes: [...state.graph.nodes, ...exp.nodes],
      edges: [...state.graph.edges, ...exp.edges],
      snippets: [...state.graph.snippets, exp.snippet],
    };
    if (attachToNodeId && attachToPortId) {
      next = addEdgeOp(next, attachToNodeId, attachToPortId, exp.entryNodeId, 'default');
    }
    state.graph = next;
    setDirty(state);
  }),
};

export default workflowEditorModel;
